import { useState, useRef, useCallback, useEffect } from 'react'
import { exclusionsService } from '@/services/exclusions-service'
import { parseSelection, serializeSelection } from '@/lib/inclusion-selection'

/* ── Inline SVG icons ── */
const DeleteIcon = () => (
  <svg style={{ width: 13, height: 13 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const CheckIcon = () => (
  <svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const DragIcon = () => (
  <svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="6" r="1" fill="currentColor" /><circle cx="15" cy="6" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="18" r="1" fill="currentColor" /><circle cx="15" cy="18" r="1" fill="currentColor" />
  </svg>
)

interface IncExlItem {
  id: string
  text: string
  type: 'inclusion' | 'exclusion'
  checked: boolean
  isCustom?: boolean // added by estimator (not from admin presets)
}

interface InclusionsExclusionsPanelProps {
  estimateId: string
  selectedInclusionIds?: string // JSON array of IDs
  selectedExclusionIds?: string // JSON array of IDs
  onSelectionChange?: (field: 'selectedInclusionIds' | 'selectedExclusionIds', ids: string) => void
}

export function InclusionsExclusionsPanel({
  estimateId,
  selectedInclusionIds,
  selectedExclusionIds,
  onSelectionChange,
}: InclusionsExclusionsPanelProps) {
  void estimateId
  const [items, setItems] = useState<IncExlItem[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'inclusions' | 'exclusions'>('inclusions')
  const [newValue, setNewValue] = useState('')
  const addInputRef = useRef<HTMLInputElement>(null)
  const initializedRef = useRef(false)

  // Parse saved selections. Each column holds preset ExclusionItem ids plus any
  // estimator-added custom items, whose text rides inline — see lib/inclusion-selection.ts.
  const savedIncl = parseSelection(selectedInclusionIds)
  const savedExcl = parseSelection(selectedExclusionIds)
  const savedInclIds = savedIncl.ids
  const savedExclIds = savedExcl.ids

  // Fetch admin-defined inclusions/exclusions on mount
  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      try {
        setLoading(true)
        const [excl, incl] = await Promise.all([
          exclusionsService.listByType('exclusion'),
          exclusionsService.listByType('inclusion'),
        ])
        if (!cancelled) {
          // Rehydrate estimator-added items from the saved selection. Only checked
          // custom items are ever persisted, so they come back checked. Without this
          // they'd vanish on reload — their text lives nowhere else.
          const rehydrateCustom = (
            texts: string[],
            type: 'inclusion' | 'exclusion',
          ): IncExlItem[] =>
            texts.map((text, i) => ({
              id: `custom-${type}-${i}`,
              text,
              type,
              checked: true,
              isCustom: true,
            }))

          const mapped: IncExlItem[] = [
            ...rehydrateCustom(savedIncl.customTexts, 'inclusion'),
            ...incl.map((item) => ({
              id: item.id,
              text: item.text,
              type: 'inclusion' as const,
              checked: savedInclIds.length > 0 ? savedInclIds.includes(item.id) : false,
              isCustom: false,
            })),
            ...rehydrateCustom(savedExcl.customTexts, 'exclusion'),
            ...excl.map((item) => ({
              id: item.id,
              text: item.text,
              type: 'exclusion' as const,
              checked: savedExclIds.length > 0 ? savedExclIds.includes(item.id) : false,
              isCustom: false,
            })),
          ]
          setItems(mapped)
          initializedRef.current = true
        }
      } catch (err) {
        console.error('Failed to load inclusions/exclusions:', err)
        // Use sample data as fallback
        setItems(SAMPLE_ITEMS)
        initializedRef.current = true
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent when selections change (skip initial load)
  const notifySelectionChange = useCallback((updatedItems: IncExlItem[]) => {
    if (!onSelectionChange || !initializedRef.current) return
    // Preset items are saved by id; custom ones by text, since they have no row to
    // point at. Saving a custom item's client-only id alone lost it — see
    // lib/inclusion-selection.ts.
    const encode = (type: 'inclusion' | 'exclusion') => {
      const checked = updatedItems.filter(i => i.type === type && i.checked)
      return serializeSelection(
        checked.filter(i => !i.isCustom).map(i => i.id),
        checked.filter(i => i.isCustom).map(i => i.text),
      )
    }
    onSelectionChange('selectedInclusionIds', encode('inclusion'))
    onSelectionChange('selectedExclusionIds', encode('exclusion'))
  }, [onSelectionChange])

  const currentType = activeTab === 'inclusions' ? 'inclusion' as const : 'exclusion' as const
  const currentItems = items.filter((item) => item.type === currentType)
  const inclusionsCount = items.filter((i) => i.type === 'inclusion').length
  const exclusionsCount = items.filter((i) => i.type === 'exclusion').length
  const checkedInclusionsCount = items.filter((i) => i.type === 'inclusion' && i.checked).length
  const checkedExclusionsCount = items.filter((i) => i.type === 'exclusion' && i.checked).length

  const handleToggleCheck = (id: string) => {
    setItems((prev) => {
      const updated = prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
      notifySelectionChange(updated)
      return updated
    })
  }

  const handleAdd = () => {
    if (!newValue.trim()) return
    const newItem: IncExlItem = {
      id: `custom-${Date.now()}`,
      text: newValue.trim(),
      type: currentType,
      checked: true, // custom items are checked by default
      isCustom: true,
    }
    setItems((prev) => {
      const updated = [newItem, ...prev]
      notifySelectionChange(updated)
      return updated
    })
    setNewValue('')
    addInputRef.current?.focus()
  }

  const handleDelete = (id: string) => {
    setItems((prev) => {
      const updated = prev.filter((item) => item.id !== id)
      notifySelectionChange(updated)
      return updated
    })
  }

  const handleSelectAll = () => {
    setItems((prev) => {
      const updated = prev.map((item) =>
        item.type === currentType ? { ...item, checked: true } : item
      )
      notifySelectionChange(updated)
      return updated
    })
  }

  const handleDeselectAll = () => {
    setItems((prev) => {
      const updated = prev.map((item) =>
        item.type === currentType ? { ...item, checked: false } : item
      )
      notifySelectionChange(updated)
      return updated
    })
  }

  /* ── Drag-to-reorder (ghost pattern) ── */
  const handleDragStart = useCallback((e: React.MouseEvent, rowId: string) => {
    e.preventDefault()
    const rowEl = (e.target as HTMLElement).closest('tr[data-row-id]') as HTMLElement
    if (!rowEl) return

    const rect = rowEl.getBoundingClientRect()
    const tableEl = rowEl.closest('table') as HTMLElement

    const clone = document.createElement('div')
    clone.style.position = 'fixed'
    clone.style.left = rect.left + 'px'
    clone.style.top = rect.top + 'px'
    clone.style.width = rect.width + 'px'
    clone.style.height = rect.height + 'px'
    clone.style.background = '#fff'
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'
    clone.style.borderRadius = '6px'
    clone.style.border = '1px solid var(--s200)'
    clone.style.zIndex = '100'
    clone.style.pointerEvents = 'none'
    clone.style.display = 'flex'
    clone.style.alignItems = 'center'
    clone.style.padding = '0 16px'
    clone.style.fontSize = '14px'
    clone.style.fontWeight = '500'
    clone.style.color = 'var(--s900)'

    const textCell = rowEl.querySelector('td:nth-child(3)') as HTMLElement
    clone.textContent = textCell?.textContent || ''
    document.body.appendChild(clone)

    rowEl.style.opacity = '0.25'

    const startY = e.clientY
    let lastSwapId: string | null = null

    const handleMouseMove = (me: MouseEvent) => {
      const dy = me.clientY - startY
      clone.style.top = (rect.top + dy) + 'px'

      if (!tableEl) return
      const rows = Array.from(tableEl.querySelectorAll('tr[data-row-id]')).filter(
        (r) => r !== rowEl
      )

      for (const r of rows) {
        const rr = r.getBoundingClientRect()
        const mid = rr.top + rr.height / 2
        const targetId = r.getAttribute('data-row-id')
        if (!targetId || targetId === rowId) continue

        if (me.clientY >= rr.top && me.clientY <= rr.bottom) {
          const ghostRect = rowEl.getBoundingClientRect()
          const dragIsAbove = ghostRect.top < rr.top

          if ((dragIsAbove && me.clientY > mid) || (!dragIsAbove && me.clientY < mid)) {
            if (lastSwapId !== targetId) {
              lastSwapId = targetId
              reorderItems(rowId, targetId, dragIsAbove)
            }
          }
          break
        }
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      clone.remove()
      rowEl.style.opacity = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [items])

  const reorderItems = (dragId: string, targetId: string, insertAfter: boolean) => {
    setItems((prev) => {
      const newItems = [...prev]
      const dragIdx = newItems.findIndex((i) => i.id === dragId)
      const targetIdx = newItems.findIndex((i) => i.id === targetId)
      if (dragIdx === -1 || targetIdx === -1) return prev
      const [dragged] = newItems.splice(dragIdx, 1)
      const newTargetIdx = newItems.findIndex((i) => i.id === targetId)
      newItems.splice(insertAfter ? newTargetIdx + 1 : newTargetIdx, 0, dragged)
      return newItems
    })
  }

  return (
    <div>
      {/* Tabs */}
      <div className="est-list-tabs" style={{ marginBottom: 0 }}>
        <button
          className={`est-list-tab ${activeTab === 'inclusions' ? 'active' : ''}`}
          onClick={() => setActiveTab('inclusions')}
        >
          Inclusions ({checkedInclusionsCount}/{inclusionsCount})
        </button>
        <button
          className={`est-list-tab ${activeTab === 'exclusions' ? 'active' : ''}`}
          onClick={() => setActiveTab('exclusions')}
        >
          Exclusions ({checkedExclusionsCount}/{exclusionsCount})
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="ie-select-btn" onClick={handleSelectAll}>Select All</button>
          <button className="ie-select-btn" onClick={handleDeselectAll}>Deselect All</button>
        </div>
      </div>

      {/* Table in card */}
      <div className="card est-list-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--s400)' }}>
            Loading...
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th style={{ width: 40 }}></th>
                <th>{activeTab === 'inclusions' ? 'Inclusion' : 'Exclusion'}</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {/* Inline add row */}
              <tr>
                <td style={{ width: 28 }}></td>
                <td style={{ width: 40 }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (newValue.trim()) {
                        handleAdd()
                      } else {
                        addInputRef.current?.focus()
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: '50%', background: 'var(--blue)', color: '#fff',
                      fontSize: 15, fontWeight: 700, lineHeight: 1,
                      border: 'none', cursor: 'pointer', padding: 0,
                    }}
                  >+</button>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      ref={addInputRef}
                      className="search-input"
                      style={{ maxWidth: 'none', flex: 1, padding: '6px 10px', fontSize: 14, border: '1px dashed var(--s200)' }}
                      placeholder={`Type a new ${currentType} and press Enter...`}
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd()
                      }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {newValue.trim() && (
                    <button
                      className="tbl-action-btn"
                      style={{ color: 'var(--teal)' }}
                      title="Add item"
                      onClick={handleAdd}
                    >
                      <CheckIcon />
                    </button>
                  )}
                </td>
              </tr>

              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--s400)' }}>
                    No {activeTab} added yet — type above to add your first item
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} data-row-id={item.id} className={item.checked ? 'ie-row-checked' : ''}>
                    {/* Drag handle */}
                    <td style={{ width: 28, padding: '0 4px' }}>
                      <span
                        style={{ cursor: 'grab', color: 'var(--s300)', display: 'inline-flex', alignItems: 'center' }}
                        onMouseDown={(e) => handleDragStart(e, item.id)}
                      >
                        <DragIcon />
                      </span>
                    </td>
                    {/* Checkbox */}
                    <td style={{ width: 40, verticalAlign: 'middle' }}>
                      <label className="ie-checkbox-wrap">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => handleToggleCheck(item.id)}
                          className="ie-checkbox"
                        />
                        <span className="ie-checkbox-visual">
                          {item.checked && <CheckIcon />}
                        </span>
                      </label>
                    </td>
                    {/* Text */}
                    <td>
                      <span
                        style={{
                          fontWeight: 500,
                          color: item.checked ? 'var(--s900)' : 'var(--s400)',
                          textDecoration: item.checked ? 'none' : 'none',
                        }}
                      >
                        {item.text}
                        {item.isCustom && (
                          <span className="ie-custom-badge">Custom</span>
                        )}
                      </span>
                    </td>
                    {/* Actions */}
                    <td style={{ textAlign: 'right' }}>
                      {item.isCustom && (
                        <button
                          className="tbl-action-btn tbl-action-btn--danger"
                          title="Remove"
                          onClick={() => handleDelete(item.id)}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

    </div>
  )
}

/* ── Sample fallback data ── */
const SAMPLE_ITEMS: IncExlItem[] = [
  { id: 'si1', text: 'All labor, materials, and equipment necessary to complete the scope of work', type: 'inclusion', checked: false },
  { id: 'si2', text: 'Traffic control and barricading during construction', type: 'inclusion', checked: false },
  { id: 'si3', text: 'Cleanup and removal of all debris upon project completion', type: 'inclusion', checked: false },
  { id: 'si4', text: 'Three-year warranty on all asphalt repairs', type: 'inclusion', checked: false },
  { id: 'se1', text: 'Concrete curb, gutter, or sidewalk repairs', type: 'exclusion', checked: false },
  { id: 'se2', text: 'Underground utility work or adjustments', type: 'exclusion', checked: false },
  { id: 'se3', text: 'Permitting fees or engineering costs', type: 'exclusion', checked: false },
  { id: 'se4', text: 'Work outside the designated project area', type: 'exclusion', checked: false },
  { id: 'se5', text: 'Damage caused by third parties after completion', type: 'exclusion', checked: false },
]
